import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Upload as UploadIcon, X, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Picker that supports both pasting an image URL and uploading a file.
 * The upload route stores the image in GridFS so it survives redeploys.
 *
 * Props:
 *   value (string)         — current image URL (or empty)
 *   onChange (url: string) — fires after upload or paste
 *   placeholder (string)
 *   testId (string)
 */
export default function ImagePicker({ value, onChange, placeholder = 'Paste image URL or upload', testId = 'image-picker' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await axios.post(`${API}/cms/media/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange?.(fullUrl);
      toast.success('Image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          data-testid={`${testId}-url`}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid={`${testId}-upload`}
        >
          {uploading ? '…' : <><UploadIcon size={13} className="mr-1" /> Upload</>}
        </Button>
        {value && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange?.('')}
            data-testid={`${testId}-clear`}
          >
            <X size={13} />
          </Button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
      />
      {value && (
        <div className="rounded border w-full h-32 bg-muted overflow-hidden">
          <img
            src={value}
            alt=""
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}
      {!value && (
        <div className="rounded border-2 border-dashed w-full h-20 grid place-items-center text-muted-foreground text-xs">
          <span className="inline-flex items-center gap-2"><ImageIcon size={14} /> No image</span>
        </div>
      )}
    </div>
  );
}
